// Medication adherence statistics (MEDICATION_COMPLIANCE_PLAN.md, D-MC-5..D-MC-14).
// Pure, deterministic, no I/O — classifies a single medication's logged doses
// against its RegimenItem.schedule (relative-to-last-dose for interval cadences,
// fixed daily/day-of-week slots otherwise), then aggregates streaks, time-of-day
// / day-of-week patterns, and a period-over-period trend. Mirrors the
// "callers pass already item-scoped, already-filtered events (see itemEvents())"
// convention from itemStats.ts — `events` is expected to already exclude
// `missed: true` entries (those are recovered here as schedule gaps instead,
// D-MC-8) and be scoped to one medication.

import type { MedicalEvent } from "./types.js";
import type { RegimenItem } from "./regimen.js";
import { DAY_PART_DEFAULT_TIME } from "./medication.js";
import { localCalendarDate } from "./datetime.js";

/** Classification of a single dose relative to its intended time and tolerance. */
export type DoseClass = "on_time" | "late" | "early" | "missed";

/** A single dose with its classification. */
export interface ClassifiedDose {
  date: string; // "YYYY-MM-DD" local calendar date
  takenAt?: string; // ISO instant (undefined if missed)
  intendedTime?: string; // "HH:MM" (undefined if not derivable)
  classifiedAs: DoseClass;
  minutesLate?: number; // for late doses; negative for early
  minutesEarly?: number; // for early doses (>30 min before intended)
}

/** Compliance statistics for a medication over a time window. */
export interface ComplianceStats {
  onTimeCount: number;
  lateCount: number;
  earlyCount: number;
  missedCount: number;
  onTimePercentage: number; // (onTime / (onTime + late + missed)) * 100

  // Streaks and patterns
  longestOnTimeStreak: number; // consecutive days with ≥1 on-time dose
  singleOnTimeDays: number; // isolated on-time days not in a streak
  averageLatenessHours?: number; // mean (actualTime - intendedTime) for late doses only

  // Time-of-day pattern: { morning: 75, afternoon: 50, ... } (percentages)
  timeOfDayPattern: Record<string, number>;

  // Day-of-week pattern: { 0: 80, 1: 90, ... } (Sun–Sat, percentages)
  dayOfWeekPattern: Record<number, number>;

  // Trend vs. previous period
  previousWindowOnTimePercentage?: number; // undefined if previous window has <2 events
  percentageChange?: number; // delta; + for improvement
  trendDirection: "up" | "down" | "flat" | "insufficient_data";

  // Raw classified doses for debugging/deep-dive
  classifiedDoses: ClassifiedDose[];

  // Metadata
  windowStartDate: string; // "YYYY-MM-DD"
  windowEndDate: string; // "YYYY-MM-DD"
}

const EARLY_THRESHOLD_MINUTES = 30;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

/** Default lateness tolerance (hours) when the patient hasn't set one (D-MC-4). */
export const DEFAULT_COMPLIANCE_TOLERANCE_HOURS = 4;

/** Clamp an arbitrary input to a valid tolerance in [0, 24], falling back to the default. */
export function resolveComplianceToleranceHours(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(Math.max(Math.round(n), 0), 24) : DEFAULT_COMPLIANCE_TOLERANCE_HOURS;
}

// ── Local-time helpers ──────────────────────────────────────────────────────
// Self-contained (shared/ can't depend on api/src/lib/suggestions.ts); mirrors
// the offset-probe technique used there and in datetime.ts's calendarDateToUtcInstant.

function localHour(iso: string, timezone: string): number {
  const h = Number(
    new Intl.DateTimeFormat("en-US", { timeZone: timezone, hour: "2-digit", hour12: false }).format(new Date(iso))
  );
  return h === 24 ? 0 : h;
}

function tzOffsetMs(date: Date, timezone: string): number {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const m: Record<string, string> = {};
  for (const p of f.formatToParts(date)) if (p.type !== "literal") m[p.type] = p.value;
  const h = Number(m.hour) === 24 ? 0 : Number(m.hour);
  const asUtc = Date.UTC(Number(m.year), Number(m.month) - 1, Number(m.day), h, Number(m.minute), Number(m.second));
  return asUtc - date.getTime();
}

/** ISO instant for local "HH:MM" on the given "YYYY-MM-DD" day key, in `timezone`. */
function localAnchorIsoForDay(dayKey: string, timezone: string, hh: number, mm: number): string {
  const [y, mo, d] = dayKey.split("-").map(Number);
  const probe = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
  const offset = tzOffsetMs(probe, timezone);
  return new Date(Date.UTC(y, mo - 1, d, hh, mm, 0) - offset).toISOString();
}

function parseHM(v: string | undefined): number | null {
  if (typeof v !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh > 23 || mm > 59) return null;
  return hh * 60 + mm;
}

function dayKeyToUtcMs(key: string): number {
  const [y, mo, d] = key.split("-").map(Number);
  return Date.UTC(y, mo - 1, d);
}

function daysBetweenKeys(a: string, b: string): number {
  return Math.round((dayKeyToUtcMs(b) - dayKeyToUtcMs(a)) / MS_PER_DAY);
}

function dayKeyWeekday(key: string): number {
  return new Date(dayKeyToUtcMs(key)).getUTCDay();
}

function shiftDayKey(dayKey: string, offsetDays: number): string {
  const [y, mo, d] = dayKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d + offsetDays));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

// ── Time-of-day buckets (D-MC-10) ───────────────────────────────────────────

export const TIME_OF_DAY_BUCKETS: {
  morning: [number, number];
  afternoon: [number, number];
  evening: [number, number];
  night: [number, number];
} = {
  morning: [5, 12],
  afternoon: [12, 17],
  evening: [17, 22],
  night: [22, 5],
};

export function getTimeOfDayBucket(hour: number): keyof typeof TIME_OF_DAY_BUCKETS {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 22) return "evening";
  return "night";
}

// ── Dose classification (D-MC-7) ────────────────────────────────────────────

/**
 * Classify a single dose (takenAt) against its intended time and tolerance.
 * On-time: from the intended time up to `toleranceHours` after it (and up to
 * 30 min before it — only doses more than 30 min early are flagged "early").
 * Late: after the tolerance window. Early: more than 30 min before intended.
 */
export function classifyDose(
  takenAt: string,
  intendedTime: string,
  toleranceHours: number,
  timezone: string
): Pick<ClassifiedDose, "classifiedAs" | "minutesLate" | "minutesEarly"> {
  const takenMs = Date.parse(takenAt);
  const hm = parseHM(intendedTime);
  if (!Number.isFinite(takenMs) || hm === null) return { classifiedAs: "on_time" };
  const dayKey = localCalendarDate(takenAt, timezone);
  const intendedMs = Date.parse(localAnchorIsoForDay(dayKey, timezone, Math.floor(hm / 60), hm % 60));
  const diffMinutes = Math.round((takenMs - intendedMs) / 60000);
  if (diffMinutes < -EARLY_THRESHOLD_MINUTES) return { classifiedAs: "early", minutesEarly: -diffMinutes };
  if (diffMinutes > toleranceHours * 60) return { classifiedAs: "late", minutesLate: diffMinutes };
  return { classifiedAs: "on_time", minutesLate: Math.max(diffMinutes, 0) };
}

// ── Scheduled times (D-MC-6) ────────────────────────────────────────────────

/**
 * Extract scheduled times for a single dose of the medication on a given date.
 * Returns an array of "HH:MM" times the medication should be taken that day, or
 * an empty array if the item has no schedule or the date is not scheduled.
 *
 * Handles: daily, daysOfWeek, everyNDays (due when the gap since
 * `previousDoseDate` reaches the cadence's `n`, or always due if no previous
 * dose is known yet — D-MC-5's "first logged dose is on-time"). everyNHours is
 * time-independent and always returns empty; interval classification for it is
 * done directly in `buildComplianceStats` against the previous dose's instant.
 */
export function getScheduledTimes(item: RegimenItem, date: string, previousDoseDate?: string): string[] {
  const schedule = item.schedule;
  if (!schedule) return [];
  const times = schedule.times?.length ? schedule.times : [DAY_PART_DEFAULT_TIME.morning];
  switch (schedule.cadence.type) {
    case "daily":
      return times;
    case "daysOfWeek":
      return schedule.cadence.days.includes(dayKeyWeekday(date)) ? times : [];
    case "everyNDays":
      if (!previousDoseDate) return times;
      return daysBetweenKeys(previousDoseDate, date) >= schedule.cadence.n ? times : [];
    case "everyNHours":
      return [];
    default:
      return [];
  }
}

// ── Window classification (shared by the current & previous-period windows) ─

interface DoseRecord {
  takenAt: string;
  date: string;
}

function classifyFixedWindow(
  item: RegimenItem,
  doses: DoseRecord[],
  dates: string[],
  toleranceHours: number,
  timezone: string
): ClassifiedDose[] {
  const dateSet = new Set(dates);
  const result: ClassifiedDose[] = [];
  const dosesByDate = new Map<string, string[]>();
  for (const d of doses) {
    if (!dosesByDate.has(d.date)) dosesByDate.set(d.date, []);
    dosesByDate.get(d.date)!.push(d.takenAt);
  }
  // Walk every date that has either a logged dose or is in the requested window
  // (not just the window) so everyNDays' gap-from-previous-dose tracking is
  // correct even when the previous dose predates the window.
  const allDates = [...new Set([...dosesByDate.keys(), ...dates])].sort();
  let previousDoseDate: string | undefined;
  for (const d of allDates) {
    const scheduledTimes = [...getScheduledTimes(item, d, previousDoseDate)].sort();
    const dayDoses = [...(dosesByDate.get(d) ?? [])].sort();
    if (scheduledTimes.length) {
      const used = new Array(dayDoses.length).fill(false);
      for (const slotTime of scheduledTimes) {
        const hm = parseHM(slotTime);
        const slotMs = hm === null ? undefined : Date.parse(localAnchorIsoForDay(d, timezone, Math.floor(hm / 60), hm % 60));
        let bestIdx = -1;
        let bestDiff = Infinity;
        dayDoses.forEach((takenAt, idx) => {
          if (used[idx] || slotMs === undefined) return;
          const diff = Math.abs(Date.parse(takenAt) - slotMs);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestIdx = idx;
          }
        });
        if (bestIdx === -1) {
          if (dateSet.has(d)) result.push({ date: d, intendedTime: slotTime, classifiedAs: "missed" });
        } else {
          used[bestIdx] = true;
          if (dateSet.has(d)) {
            const taken = dayDoses[bestIdx];
            const cls = classifyDose(taken, slotTime, toleranceHours, timezone);
            result.push({ date: d, takenAt: taken, intendedTime: slotTime, ...cls });
          }
        }
      }
    }
    if (dayDoses.length) previousDoseDate = d;
  }
  return result;
}

function classifyIntervalWindow(
  cadenceHours: number,
  doses: DoseRecord[],
  dates: string[],
  toleranceHours: number
): ClassifiedDose[] {
  const dateSet = new Set(dates);
  const result: ClassifiedDose[] = [];
  const cadenceMs = cadenceHours * MS_PER_HOUR;
  let prevTakenAt: string | undefined;
  for (const dose of doses) {
    if (!prevTakenAt) {
      // D-MC-5: no prior dose to be late/early against — the first logged dose
      // is always on-time by definition.
      if (dateSet.has(dose.date)) result.push({ date: dose.date, takenAt: dose.takenAt, classifiedAs: "on_time" });
      prevTakenAt = dose.takenAt;
      continue;
    }
    const prevMs = Date.parse(prevTakenAt);
    const takenMs = Date.parse(dose.takenAt);
    const gapMs = takenMs - prevMs;
    // A gap of >= 2 cadence periods implies one or more cycles were skipped
    // entirely (no "now" reference is available to detect a still-open miss).
    const cyclesSkipped = Math.max(0, Math.floor(gapMs / cadenceMs) - 1);
    for (let i = 0; i < cyclesSkipped; i++) {
      if (dateSet.has(dose.date)) result.push({ date: dose.date, classifiedAs: "missed" });
    }
    const dueMs = prevMs + cadenceMs;
    const diffMinutes = Math.round((takenMs - dueMs) / 60000);
    if (dateSet.has(dose.date)) {
      if (diffMinutes < -EARLY_THRESHOLD_MINUTES) {
        result.push({ date: dose.date, takenAt: dose.takenAt, classifiedAs: "early", minutesEarly: -diffMinutes });
      } else if (diffMinutes > toleranceHours * 60) {
        result.push({ date: dose.date, takenAt: dose.takenAt, classifiedAs: "late", minutesLate: diffMinutes });
      } else {
        result.push({ date: dose.date, takenAt: dose.takenAt, classifiedAs: "on_time", minutesLate: Math.max(diffMinutes, 0) });
      }
    }
    prevTakenAt = dose.takenAt;
  }
  return result;
}

function classifyWindow(
  item: RegimenItem,
  doses: DoseRecord[],
  dates: string[],
  toleranceHours: number,
  timezone: string
): ClassifiedDose[] {
  const cadence = item.schedule!.cadence;
  return cadence.type === "everyNHours"
    ? classifyIntervalWindow(cadence.hours, doses, dates, toleranceHours)
    : classifyFixedWindow(item, doses, dates, toleranceHours, timezone);
}

function computeStreaks(
  dates: string[],
  onTimeDaySet: Set<string>
): { longestOnTimeStreak: number; singleOnTimeDays: number } {
  let longest = 0;
  let singles = 0;
  let run = 0;
  const flush = () => {
    if (run === 1) singles++;
    else if (run > longest) longest = run;
    run = 0;
  };
  for (const d of dates) {
    if (onTimeDaySet.has(d)) run++;
    else flush();
  }
  flush();
  return { longestOnTimeStreak: longest, singleOnTimeDays: singles };
}

/** Build time-of-day pattern (morning/afternoon/evening/night on-time %, D-MC-10). */
export function buildTimeOfDayPattern(classifiedDoses: ClassifiedDose[], timezone: string): Record<string, number> {
  const buckets: Record<string, { onTime: number; total: number }> = {
    morning: { onTime: 0, total: 0 },
    afternoon: { onTime: 0, total: 0 },
    evening: { onTime: 0, total: 0 },
    night: { onTime: 0, total: 0 },
  };
  for (const c of classifiedDoses) {
    if (!c.takenAt || c.classifiedAs === "missed") continue;
    const bucket = getTimeOfDayBucket(localHour(c.takenAt, timezone));
    buckets[bucket].total++;
    if (c.classifiedAs === "on_time") buckets[bucket].onTime++;
  }
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(buckets)) {
    if (v.total > 0) out[k] = Math.round((v.onTime / v.total) * 100);
  }
  return out;
}

/** Build day-of-week pattern (Sun-Sat on-time %). Excludes days with <2 occurrences (D-MC-11). */
export function buildDayOfWeekPattern(classifiedDoses: ClassifiedDose[], _timezone: string): Record<number, number> {
  const buckets: Record<number, { onTime: number; total: number }> = {};
  for (let i = 0; i < 7; i++) buckets[i] = { onTime: 0, total: 0 };
  for (const c of classifiedDoses) {
    if (c.classifiedAs === "early") continue; // not a schedule-adherence failure
    const dow = dayKeyWeekday(c.date);
    buckets[dow].total++;
    if (c.classifiedAs === "on_time") buckets[dow].onTime++;
  }
  const out: Record<number, number> = {};
  for (const [k, v] of Object.entries(buckets)) {
    if (v.total >= 2) out[Number(k)] = Math.round((v.onTime / v.total) * 100);
  }
  return out;
}

/** Mean lateness (hours) across late-classified doses only (D-MC-12). */
export function getAverageLateness(classifiedDoses: ClassifiedDose[]): number | undefined {
  const lateDoses = classifiedDoses.filter((c) => c.classifiedAs === "late" && typeof c.minutesLate === "number");
  if (!lateDoses.length) return undefined;
  return Math.round(((lateDoses.reduce((sum, c) => sum + (c.minutesLate ?? 0), 0) / lateDoses.length / 60) * 10)) / 10;
}

/** Count of doses classified "early" — more than 30 min before intended time (D-MC-13). */
export function getEarlyDoseCount(classifiedDoses: ClassifiedDose[]): number {
  return classifiedDoses.filter((c) => c.classifiedAs === "early").length;
}

/** Compliance trend: current window's on-time % vs. the preceding window's (D-MC-14). */
export function buildComplianceTrend(
  currentWindowDoses: ClassifiedDose[],
  previousWindowDoses: ClassifiedDose[]
): {
  previousPercentage: number | null;
  currentPercentage: number;
  percentageChange: number;
  direction: "up" | "down" | "flat" | "insufficient_data";
} {
  const currentOnTime = currentWindowDoses.filter((c) => c.classifiedAs === "on_time").length;
  const currentLate = currentWindowDoses.filter((c) => c.classifiedAs === "late").length;
  const currentMissed = currentWindowDoses.filter((c) => c.classifiedAs === "missed").length;
  const currentDenom = currentOnTime + currentLate + currentMissed;
  const currentPercentage = currentDenom > 0 ? Math.round((currentOnTime / currentDenom) * 100) : 0;

  const prevOnTime = previousWindowDoses.filter((c) => c.classifiedAs === "on_time").length;
  const prevLate = previousWindowDoses.filter((c) => c.classifiedAs === "late").length;
  const prevMissed = previousWindowDoses.filter((c) => c.classifiedAs === "missed").length;
  const prevDenom = prevOnTime + prevLate + prevMissed;

  if (prevDenom < 2) {
    return { previousPercentage: null, currentPercentage, percentageChange: 0, direction: "insufficient_data" };
  }
  const previousPercentage = Math.round((prevOnTime / prevDenom) * 100);
  const percentageChange = currentPercentage - previousPercentage;
  const direction = percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "flat";
  return { previousPercentage, currentPercentage, percentageChange, direction };
}

// ── Main entry point ────────────────────────────────────────────────────────

/**
 * Compute compliance statistics for a medication (identified by itemIdentity scope
 * — already filtered to a single medication) over a window of local calendar dates.
 *
 * Requires:
 * - `events`: pre-filtered to a single medication, `missed: true` entries
 *   already excluded (the itemEvents() convention — see file header)
 * - `item`: the RegimenItem with its schedule (may be absent or inactive)
 * - `windowDates`: list of "YYYY-MM-DD", e.g., last 30 days
 * - `toleranceHours`: lateness tolerance from Patient prefs
 * - `timezone`: patient's timezone for time-of-day bucketing
 *
 * Returns ComplianceStats or null if no active schedule exists on the item.
 */
export function buildComplianceStats(
  events: MedicalEvent[],
  item: RegimenItem | null,
  windowDates: string[],
  toleranceHours: number,
  timezone: string
): ComplianceStats | null {
  if (!item || item.active === false || !item.schedule || !windowDates.length) return null;

  const dates = [...new Set(windowDates)].sort();
  const windowStartDate = dates[0];
  const windowEndDate = dates[dates.length - 1];

  const doses: DoseRecord[] = events
    .filter((e) => e.eventType === "medication_taken" || e.eventType === "as_needed_med")
    .map((e) => ({ takenAt: e.occurredAt, date: localCalendarDate(e.occurredAt, timezone) }))
    .filter((d) => Number.isFinite(Date.parse(d.takenAt)))
    .sort((a, b) => Date.parse(a.takenAt) - Date.parse(b.takenAt));

  const classifiedDoses = classifyWindow(item, doses, dates, toleranceHours, timezone);

  const onTimeCount = classifiedDoses.filter((c) => c.classifiedAs === "on_time").length;
  const lateCount = classifiedDoses.filter((c) => c.classifiedAs === "late").length;
  const earlyCount = classifiedDoses.filter((c) => c.classifiedAs === "early").length;
  const missedCount = classifiedDoses.filter((c) => c.classifiedAs === "missed").length;
  const denom = onTimeCount + lateCount + missedCount;
  const onTimePercentage = denom > 0 ? Math.round((onTimeCount / denom) * 100) : 0;

  const onTimeDaySet = new Set(classifiedDoses.filter((c) => c.classifiedAs === "on_time").map((c) => c.date));
  const { longestOnTimeStreak, singleOnTimeDays } = computeStreaks(dates, onTimeDaySet);

  const averageLatenessHours = getAverageLateness(classifiedDoses);

  const timeOfDayPattern = buildTimeOfDayPattern(classifiedDoses, timezone);
  const dayOfWeekPattern = buildDayOfWeekPattern(classifiedDoses, timezone);

  // Trend (D-MC-14): the immediately preceding, non-overlapping, equal-length window.
  const prevEnd = shiftDayKey(windowStartDate, -1);
  const prevStart = shiftDayKey(prevEnd, -(dates.length - 1));
  const prevDates = dates.map((_, i) => shiftDayKey(prevStart, i));
  const prevClassified = classifyWindow(item, doses, prevDates, toleranceHours, timezone);
  const trend = buildComplianceTrend(classifiedDoses, prevClassified);

  const previousWindowOnTimePercentage = trend.previousPercentage ?? undefined;
  const percentageChange = trend.direction === "insufficient_data" ? undefined : trend.percentageChange;
  const trendDirection = trend.direction;

  return {
    onTimeCount,
    lateCount,
    earlyCount,
    missedCount,
    onTimePercentage,
    longestOnTimeStreak,
    singleOnTimeDays,
    averageLatenessHours,
    timeOfDayPattern,
    dayOfWeekPattern,
    previousWindowOnTimePercentage,
    percentageChange,
    trendDirection,
    classifiedDoses,
    windowStartDate,
    windowEndDate,
  };
}
